import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Divider,
  Alert,
  CircularProgress,
  Paper,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person,
  Business,
  Email,
  Phone,
  Security,
  Edit,
  Save,
  Cancel,
  LocationOn,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';

interface ProfileData {
  firstName: string;
  lastName: string;
  email: string;
  department?: string;
  office?: string;
  position?: string;
  phone?: string;
  company: {
    name: string;
    email?: string;
    phone?: string;
    address?: string;
  };
}

interface Department {
  _id: string;
  name: string;
  description?: string;
  isActive: boolean;
}

interface Office {
  _id: string;
  name: string;
  isActive: boolean;
}

const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [offices, setOffices] = useState<Office[]>([]);
  const [loadingOffices, setLoadingOffices] = useState(false);
  const canEditOrgFields = user?.role === 'employee'
    ? (user?.company?.settings?.allowEmployeeChangeCompanyOffice ?? true)
    : true;

  // Datos editables
  const [editData, setEditData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    department: '',
    office: '',
  });

  useEffect(() => {
    fetchProfileData();
    fetchDepartments();
    fetchOffices();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      // Simular datos del perfil (en una implementación real, esto vendría del backend)
      const profile: ProfileData = {
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        department: user?.department || '',
        office: user?.office || '',
        position: user?.position || '',
        phone: user?.profile?.phone || '',
        company: {
          name: user?.company?.name || 'Empresa no asignada',
          email: user?.company?.email || '',
          phone: user?.company?.phone || '',
          address: user?.company?.address || '',
        },
      };
      
      setProfileData(profile);
      setEditData({
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone || '',
        department: profile.department || '',
        office: profile.office || '',
      });
    } catch (err: any) {
      setError('Error al cargar los datos del perfil');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      setLoadingDepartments(true);
      const departmentsList = await apiService.getDepartments();
      // Filtrar solo departamentos activos
      const activeDepartments = departmentsList.filter((dept: Department) => dept.isActive);
      setDepartments(activeDepartments);
    } catch (err: any) {
      console.error('Error cargando departamentos:', err);
      // No mostrar error al usuario, solo dejar la lista vacía
      setDepartments([]);
    } finally {
      setLoadingDepartments(false);
    }
  };

  const fetchOffices = async () => {
    try {
      setLoadingOffices(true);
      const officesList = await apiService.getOffices();
      // Filtrar solo sedes activas
      const activeOffices = officesList.filter((office: Office) => office.isActive);
      setOffices(activeOffices);
    } catch (err: any) {
      console.error('Error cargando sedes:', err);
      // No mostrar error al usuario, solo dejar la lista vacía
      setOffices([]);
    } finally {
      setLoadingOffices(false);
    }
  };

  const handleEdit = () => {
    setEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    setEditing(false);
    setEditData({
      firstName: profileData?.firstName || '',
      lastName: profileData?.lastName || '',
      phone: profileData?.phone || '',
      department: profileData?.department || '',
      office: profileData?.office || '',
    });
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');
      
      // Preparar los datos para actualizar
      const profileUpdateData: any = {
        firstName: editData.firstName,
        lastName: editData.lastName,
        profile: {
          ...user!.profile,
          phone: editData.phone || undefined,
        },
      };
      
      // Incluir department y office solo si están definidos (incluso si son strings vacíos)
      // Esto permite limpiar los campos estableciéndolos como null
      if (canEditOrgFields && editData.department !== undefined) {
        profileUpdateData.department = editData.department || null;
      }
      if (canEditOrgFields && editData.office !== undefined) {
        profileUpdateData.office = editData.office || null;
      }
      
      console.log('🔍 Datos a actualizar:', profileUpdateData);
      
      // Actualizar en el backend
      await updateUser(profileUpdateData);
      
      // Actualizar el estado local solo después de éxito
      const updatedProfile = {
        ...profileData!,
        firstName: editData.firstName,
        lastName: editData.lastName,
        phone: editData.phone,
        department: editData.department,
        office: editData.office,
      };
      
      setProfileData(updatedProfile);
      setEditing(false);
      setSuccess('Perfil actualizado exitosamente');
      
    } catch (err: any) {
      console.error('Error al actualizar perfil:', err);
      setError(err.response?.data?.message || 'Error al actualizar el perfil');
      // Mantener el modo de edición para que el usuario pueda corregir
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!profileData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Error al cargar los datos del perfil</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
          👤 Mi Perfil
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona tu información personal y de la empresa
        </Typography>
      </Box>

      {/* Mensajes */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}
      
      {!canEditOrgFields && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Tu empresa no permite que edites tu sede o departamento.
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        {/* Información Personal */}
        <Box sx={{ flex: 1 }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Person sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Información Personal
                </Typography>
                {!editing && (
                  <Button
                    startIcon={<Edit />}
                    onClick={handleEdit}
                    sx={{ ml: 'auto' }}
                    size="small"
                  >
                    Editar
                  </Button>
                )}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: 'primary.main',
                    fontSize: '2rem',
                    mr: 3,
                  }}
                >
                  {profileData.firstName.charAt(0).toUpperCase()}
                  {profileData.lastName.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {profileData.firstName} {profileData.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {profileData.position || 'Empleado'}
                  </Typography>
                  <Chip
                    label={user?.role === 'employee' ? 'Empleado' : user?.role === 'company_admin' ? 'Administrador de Empresa' : 'Administrador'}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Nombre"
                  value={editing ? editData.firstName : profileData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={!editing}
                  fullWidth
                />
                
                <TextField
                  label="Apellido"
                  value={editing ? editData.lastName : profileData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={!editing}
                  fullWidth
                />
                
                <TextField
                  label="Email"
                  value={profileData.email}
                  disabled
                  fullWidth
                  helperText="El email no se puede modificar"
                />
                
                <TextField
                  label="Teléfono"
                  value={editing ? editData.phone : profileData.phone || ''}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!editing}
                  fullWidth
                  placeholder="Ingresa tu número de teléfono"
                />
                
                <FormControl fullWidth disabled={!editing || !canEditOrgFields}>
                  <InputLabel id="office-select-label">Sede</InputLabel>
                  <Select
                    labelId="office-select-label"
                    id="office-select"
                    value={editing ? editData.office : profileData.office || ''}
                    label="Sede"
                    onChange={(e) => handleInputChange('office', e.target.value)}
                    disabled={!editing || loadingOffices || !canEditOrgFields}
                  >
                    <MenuItem value="">
                      <em>Sin sede</em>
                    </MenuItem>
                    {offices.map((office) => (
                      <MenuItem key={office._id} value={office.name}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <FormControl fullWidth disabled={!editing || !canEditOrgFields}>
                  <InputLabel id="department-select-label">Departamento</InputLabel>
                  <Select
                    labelId="department-select-label"
                    id="department-select"
                    value={editing ? editData.department : profileData.department || ''}
                    label="Departamento"
                    onChange={(e) => handleInputChange('department', e.target.value)}
                    disabled={!editing || loadingDepartments || !canEditOrgFields}
                  >
                    <MenuItem value="">
                      <em>Sin departamento</em>
                    </MenuItem>
                    {departments.map((dept) => (
                      <MenuItem key={dept._id} value={dept.name}>
                        {dept.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                <TextField
                  label="Posición"
                  value={profileData.position || ''}
                  disabled
                  fullWidth
                />
              </Box>

              {editing && (
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Cancel />}
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    Cancelar
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Información de la Empresa */}
        <Box sx={{ flex: 1 }}>
          <Card elevation={2}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Business sx={{ mr: 2, color: 'primary.main' }} />
                <Typography variant="h6" fontWeight="bold">
                  Información de la Empresa
                </Typography>
                <Chip
                  label="Solo lectura"
                  size="small"
                  color="info"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Nombre de la Empresa"
                  value={profileData.company.name}
                  disabled
                  fullWidth
                />
                
                <TextField
                  label="Email de la Empresa"
                  value={profileData.company.email || ''}
                  disabled
                  fullWidth
                />
                
                <TextField
                  label="Teléfono de la Empresa"
                  value={profileData.company.phone || ''}
                  disabled
                  fullWidth
                />
                
                <TextField
                  label="Dirección"
                  value={profileData.company.address || ''}
                  disabled
                  fullWidth
                  multiline
                  rows={3}
                />
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Cambio de Contraseña */}
      <Card elevation={2} sx={{ mt: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Security sx={{ mr: 2, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Seguridad
            </Typography>
          </Box>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Para cambiar tu contraseña, contacta a tu administrador de empresa.
          </Typography>
          
          <Button
            variant="outlined"
            color="primary"
            disabled
          >
            Cambiar Contraseña
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Profile;
